import { ChangeEvent, FC, useMemo, useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    ModalFooter,
    Text,
    useDisclosure,
    Spinner,
    Input,
    VStack,
    IconButton,
    useToast,
    Icon,
    Select,
    Alert,
    AlertIcon,
    NumberInputField,
    NumberInput,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    FormControl,
    FormErrorMessage,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    HStack,
    Progress,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { AlertTriangle } from 'react-feather';
import {
    ColumnDef,
    SortingFn,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import FileSaver from 'file-saver';

import { iconTableStyle, linkStyle, tableToolbarStyle } from 'shared/styles';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import { DITable } from 'plugins/DITable';
import {
    useAddClientKeywordMutation,
    useDeleteClientKeywordsMutation,
    useGetKeywordsQuery,
    useLazyExportClientKeywordsQuery,
    useUpdateClientKeywordsMutation,
} from 'core/API/keywordsApi';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetKeywordsLimits } from 'core/redux/selectors/authSelectors';
import {
    TKeyword,
    keyable,
    IVisibleKeyword,
    keywordAffiliation,
    IExtendedKeyword,
    IUserInfo,
    ICompanyInfo,
} from 'types';
import TablePagination from 'plugins/DITable/Pagination';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import SelectColumnFilter from 'plugins/DITable/filters/SelectFilter';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { isFetchBaseQueryError } from 'utils/erros';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import BooleanColumnFilter from 'plugins/DITable/filters/BooleanColumnFilter';
import AutoCompleteSelect from 'components/atoms/AutoCompleteSelect';
import RSelect from 'components/atoms/formInputs/RSelect';
import { EMPTY_KEYWORD, EMPTY_PARTIAL_KEYWORD, FILTER_ALL_KEY, KEYWORDS_COLUMNS } from './constants';
import FilterPanel from '../../FilterPanel';

const MKeywordsViewer: FC<{
    keywordsType?: keywordAffiliation;
    readonly: boolean;
    userInfo?: IUserInfo;
    companyInfo?: ICompanyInfo;
}> = ({ keywordsType, readonly, userInfo, companyInfo }) => {
    const isAlcoholSeller = companyInfo?.alcohol_sellers;
    const [keywordToAdd, setKeywordToAdd] = useState(keywordsType === 'search' ? EMPTY_KEYWORD : EMPTY_PARTIAL_KEYWORD);
    const [keywordToUpdate, setKeywordToUpdate] = useState<TKeyword | null>(null);
    const [keywordToDelete, setKeywordToDelete] = useState<TKeyword | null>(null);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [languages, setLanguages] = useState<string[]>([]);
    const [selectedKeywords, setSelectedKeywords] = useState<number[]>([]);
    const [isDeleteAll, setIsDeleteAll] = useState(false);
    const [filters, setFilters] = useState<{ id: string; value: string }[]>([]);
    const [typeOptions, setTypeOptions] = useState<string[]>([]);
    const [panelFilters, setPanelFilters] = useState<string | undefined>(FILTER_ALL_KEY);
    const alcoholOptions = [
        {
            value: 'no alcohol',
            translationKey: 'keyword.column.alcohol_value.no_alcohol',
        },
        {
            value: 'low alcohol',
            translationKey: 'keyword.column.alcohol_value.low_alcohol',
        },
        {
            value: 'spirit',
            translationKey: 'keyword.column.alcohol_value.spirit',
        },
    ];

    const { data: keywordsData, isError, isLoading } = useGetKeywordsQuery({});
    // const { data: keywordsOtherTypeData } = useGetKeywordsQuery({});
    const [exportKeywords, { isLoading: isExportLoading, isFetching }] = useLazyExportClientKeywordsQuery();
    const [updateKeywords, { isLoading: isUpdateLoading }] = useUpdateClientKeywordsMutation();
    const [deleteKeywords, { isLoading: isDeleteLoading }] = useDeleteClientKeywordsMutation();

    const [addKeyword, { isLoading: isAddLoading }] = useAddClientKeywordMutation();
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const keywordsLimits = useGetKeywordsLimits();
    const keywordUsage = (['search', 'banner'] as const).map((type) => ({
        type,
        count: keywordsData?.keywords.filter((keyword) => keyword[`for_${type}`]).length ?? 0,
        limit: keywordsLimits[`${type}Limit`] as number | null | undefined,
    }));

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const translate = useTranslation();
    const toast = useToast();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
            setKeywordToAdd({ ...keywordToAdd, language: langsArr[0] });
        }
    }, [retailers, isRetailersLoading]);

    const data: Readonly<any[]> = useMemo(() => {
        if (!isLoading && !isError && keywordsData) {
            const types: Set<string> = new Set<string>();
            keywordsData.keywords.forEach((k) => {
                if (k.keyword_type !== '') types.add(k.keyword_type);
            });
            setTypeOptions(Array.from(types));

            return keywordsData.keywords;
        }
        return [];
    }, [isLoading, isError, keywordsData]);

    const getKeywordTagNames = (tagIds: (string | number)[] = []) =>
        tagIds.map(
            (tagId) =>
                keywordsData?.keywordTags?.find((tag) => String(tag.id) === String(tagId))?.name ?? String(tagId),
        );

    const handleEditKeyword = (keyword: TKeyword) => {
        setModalType('update');
        setKeywordToUpdate({ ...keyword, tags: getKeywordTagNames(keyword.tags) });
        setKeywordToDelete(keyword);
        onOpen();
    };

    const handleOpenDeleteKeyword = () => {
        onClose();
        onDeleteOpen();
    };

    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: number) => {
        if (event.target.checked) setSelectedKeywords([...selectedKeywords, id]);
        else setSelectedKeywords(selectedKeywords.filter((e) => e !== id));
    };

    const filteredData = useMemo(() => {
        return data.filter((row) => {
            if (panelFilters === FILTER_ALL_KEY) return true;
            if (!panelFilters) return !row.tags?.length;

            return row.tags?.some((tag: string) => String(tag) === String(panelFilters));
        });
    }, [data, panelFilters]);

    const visibleKeywordIds = useMemo(() => filteredData.map((k) => k.id), [filteredData]);
    const selectedVisibleKeywordIds = useMemo(
        () => selectedKeywords.filter((id) => visibleKeywordIds.includes(id)),
        [selectedKeywords, visibleKeywordIds],
    );

    useEffect(() => {
        setSelectedKeywords((prevSelected) => {
            const nextSelected = prevSelected.filter((id) => visibleKeywordIds.includes(id));

            return nextSelected.length === prevSelected.length ? prevSelected : nextSelected;
        });
    }, [visibleKeywordIds]);

    const handleCheckAllRows = () => {
        if (visibleKeywordIds.length === 0) return;

        const selectedVisibleCount = visibleKeywordIds.filter((id) => selectedKeywords.includes(id)).length;
        const shouldSelectAllVisible = selectedVisibleCount < visibleKeywordIds.length;

        setSelectedKeywords((prevSelected) => {
            if (shouldSelectAllVisible) {
                return Array.from(new Set([...prevSelected, ...visibleKeywordIds]));
            }

            return prevSelected.filter((id) => !visibleKeywordIds.includes(id));
        });
    };

    const columns: ColumnDef<any, any>[] = useMemo(() => {
        const nbKeywords = visibleKeywordIds.length;
        const selectedVisibleCount = visibleKeywordIds.filter((id) => selectedKeywords.includes(id)).length;
        const tableColumns: (ColumnDef<any, any> | null)[] = [
            ...(readonly
                ? []
                : [
                      {
                          header: () => (
                              <Center m={0}>
                                  <Checkbox
                                      onChange={handleCheckAllRows}
                                      isIndeterminate={selectedVisibleCount > 0 && selectedVisibleCount < nbKeywords}
                                      isChecked={nbKeywords > 0 && selectedVisibleCount === nbKeywords}
                                  ></Checkbox>
                              </Center>
                          ),
                          id: 'selector',
                          enableColumnFilter: false,
                          cell: (info) => {
                              const keywordOriginal = info.row.original as TKeyword;
                              return (
                                  <Center>
                                      <Checkbox
                                          onChange={(e) => {
                                              // will uncomment when read_only is implemented
                                              // if (!keywordOriginal.read_only)
                                              handleCheckRow(e, keywordOriginal.id);
                                          }}
                                          isChecked={selectedKeywords.includes(keywordOriginal.id)}
                                      ></Checkbox>
                                  </Center>
                              );
                          },
                          meta: { width: '5%' },
                      } as ColumnDef<any, any>,
                  ]),
            {
                header: translate('keyword.column.keyword') as string,
                accessorKey: 'name',
                sortingFn: 'caseInsensitive',
                meta: { width: '20%' },
                cell: (info) => {
                    const { retailer_weights, ...keywordsRow } = info.row.original as TKeyword;
                    return (
                        <Box
                            onClick={() => {
                                // will uncomment when read_only is implemented in UAM
                                // if (!keywordsRow.read_only) {
                                handleEditKeyword(keywordsRow);
                                // }
                            }}
                        >
                            {
                                //  ts-ignore will be removed when read_only is implemented
                                // @ts-ignore
                                keywordsRow.read_only ? (
                                    <Tooltip label="you can't modify this keyword">
                                        <Text {...linkStyle} cursor="default" color="gray.400">
                                            {info.getValue() as string}
                                        </Text>
                                    </Tooltip>
                                ) : (
                                    <Text {...linkStyle}>{info.getValue() as string}</Text>
                                )
                            }
                        </Box>
                    );
                },
            } as ColumnDef<any, any>,
            {
                header: translate('keyword.column.useage') as string,
                accessorFn: (row) => {
                    const { for_banner, for_search } = (row || {}) as TKeyword;

                    return for_banner && for_search
                        ? translate('keyword.useage.both')
                        : for_banner
                        ? translate('keyword.useage.banner')
                        : translate('keyword.useage.search');
                },
                meta: {
                    width: '20%',
                    Filter: (props) => (
                        <SelectColumnFilter
                            {...props}
                            customOptions={[
                                translate('keyword.useage.both'),
                                translate('keyword.useage.search'),
                                translate('keyword.useage.banner'),
                            ]}
                        />
                    ),
                },
                sortingFn: 'caseInsensitive',
            } as ColumnDef<any, any>,
            {
                header: translate('keyword.column.type') as string,
                accessorKey: 'keyword_type',
                sortingFn: 'caseInsensitive',
                meta: { width: '20%', Filter: SelectColumnFilter },
                filterFn: 'equals',
            } as ColumnDef<any, any>,
            languages.length !== 1
                ? ({
                      header: translate('keyword.column.language') as string,
                      accessorKey: 'language',
                      meta: { Filter: SelectColumnFilter },
                      filterFn: 'equals',
                      sortingFn: 'caseInsensitive',
                  } as ColumnDef<any, any>)
                : null,
            {
                header: translate('keyword.column.criticality') as string,
                accessorKey: 'priority',
            } as ColumnDef<any, any>,
            ...(isAlcoholSeller
                ? [
                      {
                          header: translate('keyword.column.alcohol') as string,
                          accessorKey: 'alcohol',
                          sortingFn: 'caseInsensitive',
                          meta: { width: '20%', Filter: SelectColumnFilter },
                          filterFn: 'equals',
                          cell: (info) => {
                              return (
                                  <Box>
                                      <Text>
                                          {translate(
                                              `keyword.column.alcohol_value.${(info.getValue() as string)
                                                  ?.trim()
                                                  ?.split(' ')
                                                  ?.join('_')}`,
                                          )}
                                      </Text>
                                  </Box>
                              );
                          },
                      } as ColumnDef<any, any>,
                  ]
                : []),
            {
                header: translate('keyword.column.master') as string,
                accessorKey: 'master',
                meta: { Filter: BooleanColumnFilter },
                enableSorting: false,
                cell: (info) => <Checkbox isDisabled isChecked={info.getValue() === 1} />,
            } as ColumnDef<any, any>,
            userInfo?.is_international === true
                ? ({
                      header: translate('keyword.column.intScorecard') as string,
                      accessorKey: 'for_int_scorecard',
                      meta: { Filter: BooleanColumnFilter },
                      enableSorting: false,
                      cell: (info) => <Checkbox isDisabled isChecked={info.getValue() as boolean} />,
                  } as ColumnDef<any, any>)
                : null,
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<any, any> => e !== null);
        return filteredColumns;
    }, [selectedKeywords, visibleKeywordIds, languages, userInfo, readonly]);

    const defaultColumn = useMemo(
        () => ({
            meta: { Filter: DefaultColumnFilter },
        }),
        [],
    );

    const table = useReactTable({
        columns,
        data: filteredData,
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters as { id: string; value: string }[]);
    }, [table.getState().columnFilters]);

    const handleAddKeywordOpen = () => {
        setModalType('add');
        onOpen();
    };

    const handleCloseAddAndUpdateModal = () => {
        if (modalType === 'add') setKeywordToAdd({ ...EMPTY_KEYWORD, language: languages[0] });
        else setKeywordToUpdate(null);
        onClose();
    };

    const renderTableToolbar = () => {
        const setGlobalFilter = table.setGlobalFilter;

        const handleExportKeywords = async (e: any, isDouble = false) => {
            try {
                const { data: res, isError: isExportError } = await exportKeywords({
                    types: ['search', 'banner'],
                });
                if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });
                FileSaver.saveAs(blob, translate('keyword.export.fileName', { type: keywordsType }) as string);
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleDelete = () => {
            setIsDeleteAll(true);
            onDeleteOpen();
        };

        const PlusIcon = icons.plus;
        const DownloadIcon = icons.download;
        return (
            <Flex {...tableToolbarStyle} flexWrap="wrap" rowGap={2}>
                <GlobalFilter globalFilter={table.getState().globalFilter} setGlobalFilter={setGlobalFilter} />
                <IconButton
                    onClick={intentionalyFloatingPromiseReturn((e) => handleExportKeywords(e, true))}
                    {...iconTableStyle}
                    aria-label="download excel"
                    isDisabled={isLoading || isError || (keywordsData && keywordsData.keywords.length === 0)}
                    isLoading={isExportLoading || isFetching}
                    size="sm"
                    icon={<Icon as={DownloadIcon} />}
                />
                <IconButton
                    onClick={handleAddKeywordOpen}
                    {...iconTableStyle}
                    aria-label="add alias"
                    isDisabled={
                        readonly || isRetailersLoading
                        // ||
                        // (keywordsData &&
                        //     keywordsLimits &&
                        //     keywordsLimits[`${keywordsType}Limit`] &&
                        //     keywordsData.keywords.length >= keywordsLimits[`${keywordsType}Limit`])
                    }
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                />

                <IconButton
                    size="sm"
                    isDisabled={readonly || selectedVisibleKeywordIds.length === 0}
                    {...iconTableStyle}
                    onClick={handleDelete}
                    aria-label="bulk delete"
                    icon={<Icon as={DeleteIcon} />}
                />
                {keywordsData && !isError && (
                    <Flex ml="auto" gap={2} flexWrap="wrap" justifyContent="flex-end" alignItems="center">
                        {keywordUsage.map(({ type, count, limit }) => {
                            const label = translate(`keyword.useage.${type}`) as string;
                            const usage = `${count} / ${limit ?? '\u221e'}`;
                            const isAtLimit = limit != null && count >= limit;
                            const usageColor = isAtLimit ? 'red.500' : type === 'search' ? '#295ba8' : '#a86e24';

                            return (
                                <Flex
                                    key={type}
                                    alignItems="center"
                                    gap={2}
                                    fontSize="xs"
                                    px={3}
                                    py={2}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    bg="white"
                                    borderRadius="full"
                                >
                                    <Text whiteSpace="nowrap">{label}</Text>
                                    <Progress
                                        aria-label={label}
                                        aria-valuetext={usage}
                                        value={limit == null ? 0 : count}
                                        max={Math.max(limit ?? 0, 1)}
                                        w={{ base: '56px', md: '80px' }}
                                        flexShrink={0}
                                        size="xs"
                                        borderRadius="sm"
                                        sx={{ '& [role="progressbar"]': { bg: usageColor } }}
                                    />
                                    <Text
                                        whiteSpace="nowrap"
                                        fontWeight="normal"
                                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                                    >
                                        <Text as="span" color={usageColor} fontWeight="bold">
                                            {count}
                                        </Text>
                                        {` / ${limit ?? '\u221e'}`}
                                    </Text>
                                </Flex>
                            );
                        })}
                    </Flex>
                )}
            </Flex>
        );
    };

    const handleChangeUpdateKeyword = (value: string | boolean | number | string[], type: keyof IVisibleKeyword) => {
        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        switch (type) {
            case 'master':
            case 'for_int_scorecard':
                if (modalType === 'add') {
                    setKeywordToAdd((current) => ({ ...current, [type]: !current[type] }));
                } else {
                    setKeywordToUpdate((current) => (current ? { ...current, [type]: !current[type] } : current));
                }
                break;
            default:
                if (modalType === 'add') {
                    setKeywordToAdd((current) => ({ ...current, [type]: value }));
                } else {
                    setKeywordToUpdate((current) => (current ? { ...current, [type]: value } : current));
                }
        }
    };

    const handleChangeNumberInputKeyword = (val: string, type: keyof IVisibleKeyword) => {
        if (modalType === 'add') setKeywordToAdd({ ...keywordToAdd, [type]: val });
        else setKeywordToUpdate({ ...keywordToUpdate!, [type]: val });
    };

    const renderAttributeByType = (type: keyof IVisibleKeyword, columnName: LocaleKey) => {
        const keywordToUse = modalType === 'add' ? keywordToAdd : keywordToUpdate;
        switch (type) {
            case 'master':
            case 'for_int_scorecard':
                return type === 'for_int_scorecard' && !userInfo?.is_international ? null : (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Checkbox
                                isDisabled={readonly}
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                isChecked={Boolean(keywordToUse![type])}
                            ></Checkbox>
                        </Flex>
                    </Flex>
                );
            case 'useage':
                return (
                    <>
                        <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                            <Text w="30%" pr={2}>
                                {translate('keyword.useage')}
                            </Text>
                            <Flex flexDirection="row" flex="1" gap="2">
                                <Text w="30%">{translate('keyword.useage.search')}</Text>
                                <Flex>
                                    <Checkbox
                                        isDisabled={readonly}
                                        onChange={(e) => handleChangeUpdateKeyword(e.target.checked, 'for_search')}
                                        isChecked={Boolean(keywordToUse!['for_search'])}
                                    ></Checkbox>
                                </Flex>
                                <Text w="30%">{translate('keyword.useage.banner')}</Text>
                                <Flex>
                                    <Checkbox
                                        isDisabled={readonly}
                                        onChange={(e) => handleChangeUpdateKeyword(e.target.checked, 'for_banner')}
                                        isChecked={Boolean(keywordToUse!['for_banner'])}
                                    ></Checkbox>
                                </Flex>
                            </Flex>
                        </Flex>
                    </>
                );
            case 'keyword_type':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <AutoCompleteSelect
                                size="sm"
                                mutable={true}
                                onChange={(value) => handleChangeUpdateKeyword(value as string, type)}
                                value={keywordToUse!['keyword_type']}
                                addOption={(option) => {
                                    setTypeOptions([...typeOptions, option]);
                                }}
                                readonly={readonly}
                                isClearable={false}
                            >
                                {typeOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </AutoCompleteSelect>
                        </Flex>
                    </Flex>
                );

            case 'tags':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <RSelect
                            options={
                                keywordsData?.keywordTags?.map((tag) => ({
                                    label: tag.name,
                                    value: tag.name,
                                })) || []
                            }
                            onChange={(values) => handleChangeUpdateKeyword(values.map(String), type)}
                            selectedOptions={keywordToUse?.tags || []}
                            disabled={readonly}
                            isCreatable
                        />
                    </Flex>
                );

            case 'alcohol':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Select
                                size="sm"
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                isDisabled={readonly}
                                value={keywordToUse![type]}
                            >
                                {alcoholOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {translate(option.translationKey)}
                                    </option>
                                ))}
                            </Select>
                        </Flex>
                    </Flex>
                );
            case 'language':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Select
                                size="sm"
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                isDisabled={readonly}
                                value={keywordToUse![type]}
                            >
                                {languages.map((l) => (
                                    <option key={l} value={l}>
                                        {l}
                                    </option>
                                ))}
                            </Select>
                        </Flex>
                    </Flex>
                );
            case 'priority':
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <FormControl isInvalid={keywordToUse![type] > 99 || keywordToUse![type] < 0}>
                                <NumberInput
                                    onChange={(v) => handleChangeNumberInputKeyword(v, type)}
                                    value={keywordToUse![type]}
                                    size="sm"
                                    w="100%"
                                    defaultValue={0}
                                    min={1}
                                    max={99}
                                    clampValueOnBlur={false}
                                    isDisabled={readonly}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                                {(keywordToUse![type] > 99 || keywordToUse![type] < 0) && (
                                    <FormErrorMessage> {translate('keyword.modal.criticalityError')} </FormErrorMessage>
                                )}
                            </FormControl>
                        </Flex>
                    </Flex>
                );

            default:
                return (
                    <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                        <Text w="30%">{translate(columnName)}</Text>
                        <Flex flex="1">
                            <Input
                                size="sm"
                                onChange={(e) => handleChangeUpdateKeyword(e.target.value, type)}
                                value={keywordToUse![type] as string | number | undefined}
                                isDisabled={readonly}
                            />
                        </Flex>
                    </Flex>
                );
        }
    };

    const handleUpdateKeywords = async () => {
        try {
            const isNameExists = keywordsData?.keywords.find(
                (k) =>
                    k.name === keywordToUpdate?.name.trim() &&
                    k.language === keywordToUpdate.language &&
                    k.keyword_id !== keywordToUpdate.keyword_id,
            );

            if (isNameExists)
                throw new Error(
                    translate('keyword.modal.exisitsError', { name: keywordToUpdate!.name.trim() }) as string,
                );

            const checkedKeyword: keyable = { ...keywordToUpdate! };
            Object.entries(checkedKeyword).forEach(([key, val]) => {
                if (typeof val === 'string') {
                    checkedKeyword[key] = val.trim();
                }
            });
            if (Array.isArray(checkedKeyword.tags)) {
                checkedKeyword.tags = checkedKeyword.tags.map((tag: string) => tag.trim());
            }

            const res = await updateKeywords({ keyword: checkedKeyword as TKeyword, type: keywordsType });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    if (errorObj.status === 400 && (errorObj.data as any)?.type === 'unauthorized_read_only_edit') {
                        throw new Error(translate('keyword.modal.readOnlyError') as string);
                    }
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as any).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('keyword.modal.updateError') as string);
            }

            handleCloseAddAndUpdateModal();
            toast({
                title: translate('keyword.modal.updateSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleAddKeyword = async () => {
        try {
            const exisitingKeyword = keywordsData?.keywords.find(
                (k) => k.name === keywordToAdd.name.trim() && k.language === keywordToAdd.language,
            ) as IExtendedKeyword | undefined;

            if (exisitingKeyword)
                throw new Error(translate('keyword.modal.exisitsError', { name: keywordToAdd.name.trim() }) as string);

            const checkedKeyword: keyable = { ...keywordToAdd };
            Object.entries(checkedKeyword).forEach(([key, val]) => {
                if (typeof val === 'string') {
                    checkedKeyword[key] = val.trim();
                }
            });
            if (Array.isArray(checkedKeyword.tags)) {
                checkedKeyword.tags = checkedKeyword.tags.map((tag: string) => tag.trim());
            }

            const res = await addKeyword(checkedKeyword as IVisibleKeyword);

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as any).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('keyword.modal.addError') as string);
            }

            handleCloseAddAndUpdateModal();
            toast({
                title: translate('keyword.modal.addSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderUpdateAndAddModal = () => {
        let attributes = KEYWORDS_COLUMNS.filter(
            (c) =>
                (c.id !== 'alcohol' || isAlcoholSeller) && (c.id !== 'for_int_scorecard' || userInfo?.is_international),
        );

        const keywordToUse = modalType === 'add' ? keywordToAdd : keywordToUpdate;

        return (
            <Modal initialFocusRef={updateInitialRef} isOpen={isOpen} onClose={handleCloseAddAndUpdateModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                        {translate(modalType === 'update' ? 'keyword.modal.titleUpdate' : 'keyword.modal.titleAdd')}
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading} />
                    {modalType === 'update' && keywordToUpdate?.for_banner && keywordToUpdate?.for_search && (
                        <Alert status="info">
                            <AlertIcon />
                            {translate('keyword.modal.updateAlert')}
                        </Alert>
                    )}
                    <ModalBody>
                        {keywordToUse && (
                            <VStack>{attributes.map((attr) => renderAttributeByType(attr.id, attr.name))}</VStack>
                        )}
                    </ModalBody>
                    <ModalFooter fontSize="sm" justifyContent="space-between">
                        {modalType === 'update' && !readonly && (
                            <Box {...linkStyle} color="red.500" onClick={handleOpenDeleteKeyword}>
                                {translate('keyword.modal.delete')}
                            </Box>
                        )}
                        <Flex flex="1" justifyContent="flex-end">
                            <Button
                                isDisabled={modalType === 'add' ? isAddLoading : isUpdateLoading}
                                size="sm"
                                variant="outline"
                                mr={readonly ? 0 : 3}
                                onClick={handleCloseAddAndUpdateModal}
                            >
                                {translate('keyword.modal.cancel')}
                            </Button>
                            {!readonly && (
                                <Button
                                    onClick={
                                        modalType === 'add'
                                            ? intentionalyFloatingPromiseReturn(handleAddKeyword)
                                            : intentionalyFloatingPromiseReturn(handleUpdateKeywords)
                                    }
                                    isLoading={modalType === 'add' ? isAddLoading : isUpdateLoading}
                                    isDisabled={modalType === 'add' && !keywordToAdd.name.trim()}
                                    // isDisabled={
                                    //     modalType === 'add' &&
                                    //     keywordsData &&
                                    //     keywordsLimits[`${keywordsType}Limit`] &&
                                    //     keywordsData.keywords.length >= keywordsLimits[`${keywordsType}Limit`]
                                    // }
                                    size="sm"
                                    colorScheme="blue"
                                    bg="blueMain"
                                >
                                    {translate('keyword.modal.apply')}
                                </Button>
                            )}
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderDeleteModal = () => {
        const handleDeleteKeyword = async () => {
            try {
                const payload = isDeleteAll
                    ? selectedVisibleKeywordIds.map((k) => ({ id: k }))
                    : [{ id: keywordToDelete!.id }];

                if (isDeleteAll && payload.length === 0) {
                    onDeleteClose();
                    return;
                }

                await deleteKeywords({
                    keywords: payload,
                }).unwrap();

                if (isDeleteAll) {
                    setSelectedKeywords((prevSelected) =>
                        prevSelected.filter((id) => !selectedVisibleKeywordIds.includes(id)),
                    );
                }
                onDeleteClose();
                toast({
                    title: translate('keyword.modal.deleteSuccesful'),
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: translate('keyword.modal.deleteError', { err: (error as Error).message }),
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleCloseDelete = () => {
            setIsDeleteAll(false);
            onDeleteClose();
        };

        return (
            <Modal isCentered initialFocusRef={deleteInitialRef} isOpen={isDeleteOpen} onClose={handleCloseDelete}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={deleteInitialRef}>
                        <Text as="h3">
                            {translate(
                                isDeleteAll ? 'keyword.modal.titleDeleteSingle' : 'keyword.modal.titleDeletePlural',
                            )}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isDeleteLoading} />
                    <ModalBody fontSize="sm">
                        <Text>{translate('keyword.modal.deleteInfo')}</Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            isDisabled={isDeleteLoading}
                            variant="outline"
                            size="sm"
                            mr={3}
                            onClick={handleCloseDelete}
                        >
                            {translate('keyword.modal.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleDeleteKeyword)}
                            isLoading={isDeleteLoading}
                            colorScheme="red"
                            size="sm"
                        >
                            {translate('keyword.modal.deleteAction')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const globalFilters = useMemo(
        () => [
            { title: translate('keyword.filter.all') as string, value: FILTER_ALL_KEY, nb: data?.length || 0 },
            {
                title: translate('keyword.filter.unassigned') as string,
                value: undefined,
                nb: keywordsData?.unassignedCount || 0,
            },
        ],
        [data, keywordsData],
    );

    const otherFilters = useMemo(
        () =>
            keywordsData?.keywordTags?.map((tag) => ({
                title: tag.name,
                value: `${tag.id}`,
                nb: tag.nb || 0,
            })) || [],
        [keywordsData],
    );

    return (
        <Flex mt="16px" gap="8px" w="100%">
            <FilterPanel
                globalFilters={globalFilters}
                otherFilters={otherFilters}
                activeFilter={panelFilters}
                setActiveFilter={setPanelFilters}
            />
            <Flex direction="column" flexGrow={1}>
                {renderTableToolbar()}
                {!isLoading && !isError && keywordsData && (
                    <Flex direction={{ base: 'column', lg: 'row' }} alignItems="stretch">
                        {keywordUsage.map(({ type, count, limit }) => {
                            if (limit == null || count < limit * 0.9) return null;

                            const isAtLimit = count >= limit;
                            const label = translate(`keyword.useage.${type}`) as string;

                            return (
                                <Alert
                                    key={type}
                                    status={isAtLimit ? 'error' : 'warning'}
                                    aria-label={label}
                                    fontSize="sm"
                                    flex="1"
                                    minW={0}
                                    _notFirst={{
                                        borderColor: 'gray.300',
                                        borderTopWidth: { base: '1px', lg: 0 },
                                        borderLeftWidth: { base: 0, lg: '1px' },
                                    }}
                                >
                                    <Icon
                                        as={AlertTriangle}
                                        color={isAtLimit ? 'red.500' : 'orange.500'}
                                        boxSize={5}
                                        mr={3}
                                        flexShrink={0}
                                        aria-hidden="true"
                                    />
                                    <Text color="#563b0d">
                                        <Text as="span" fontWeight="bold">
                                            {label}
                                        </Text>
                                        {' : '}
                                        <Text as="span">
                                            {translate(isAtLimit ? 'keyword.limit.reached' : 'keyword.limit.near', {
                                                count,
                                                limit,
                                                remaining: Math.max(limit - count, 0),
                                            })}
                                        </Text>
                                    </Text>
                                </Alert>
                            );
                        })}
                    </Flex>
                )}
                {isLoading || isRetailersLoading ? (
                    <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                        <Spinner />
                    </Center>
                ) : (
                    <>
                        <DITable table={table} isPagination isHeaderFixed />
                        <TablePagination table={table} />
                    </>
                )}
                {renderUpdateAndAddModal()}
                {renderDeleteModal()}
            </Flex>
        </Flex>
    );
};

export default MKeywordsViewer;
